/**
 * Pure view model for one row of the Einheiten list (Units redesign, Slice 1).
 *
 * This module translates ONE unit plus the already-loaded rooms/tenancies into
 * the stable shape the later modern list row renders. It is deliberately pure:
 * no React, no fetching, no DOM, no storage, no clock of its own beyond what the
 * occupancy helpers already own.
 *
 * WHAT IT DOES NOT DO. It introduces no new business rules. Segment, occupancy,
 * display id and money all come from the helpers that already own them today, so
 * there is exactly one place where each of those rules lives:
 *
 *   segment     normalizeUnitTypeLabel      (utils/unitDisplayId)
 *   displayId   getDisplayUnitId            (utils/unitDisplayId)
 *   occupancy   getUnitOccupancyStatus      (utils/unitOccupancyStatus)
 *   room counts canonical Layer-1 snapshots on the unit row, via
 *               getCoLivingMetrics / coLivingRoomCount (OC2b) — never
 *               the declared `unit.rooms` field
 *   revenue     getCoLivingMetrics          (co-living) / unit.current_revenue_chf
 *                                           (business apartment) — see MONEY below
 *   run-rate    unit_running_costs_service  (backend, F2a) — read, never recomputed
 *
 * MONEY IS SEGMENT-DEPENDENT, AND THAT IS TODAY'S BEHAVIOUR, NOT A NEW RULE.
 * The co-living side reads `getCoLivingMetrics(...).currentRevenue`, the
 * business side `unit.current_revenue_chf` — each the source its own table read
 * before Slices 6 and 7 replaced them with lists. Those are two different
 * numbers under the same column heading, and this model reproduces the split
 * verbatim rather than picking a winner. Unifying them is a product decision,
 * not a refactor.
 *
 * SEGMENT DIVERGENCE, PINNED NOT FIXED (Regel 41). `AdminApartmentsPage` splits
 * its two tables on a RAW string compare (`item.type === "Co-Living"`), while
 * every domain helper — occupancy, display id, the unit form hook — normalises
 * first. A unit whose type carries a non-ASCII hyphen or no space is Co-Living to
 * the helpers and falls out of BOTH tables in the page. This model follows the
 * helpers, because they are the named contract and the raw compare is the
 * outlier. The difference is characterised in
 * `unitListSemantics.characterization.test.js` so the later list slice adopts it
 * knowingly.
 */

import {
  getDisplayUnitId,
  normalizeUnitTypeLabel,
} from "../../../utils/unitDisplayId";
import {
  activeRoomsForUnit,
  canonicalUnitRoomCounts,
  formatOccupancyStatusDe,
  getUnitOccupancyStatus,
  hasOccupancySnapshotError,
} from "../../../utils/unitOccupancyStatus";
import {
  getCoLivingMetrics,
  getRoomsForUnit,
} from "../../../utils/adminUnitCoLivingMetrics";
import { formatDisplayAddress } from "../../../utils/formatDisplayAddress";

/** @typedef {"co_living" | "business_apartment"} UnitSegment */

export const SEGMENT_CO_LIVING = "co_living";
export const SEGMENT_BUSINESS_APARTMENT = "business_apartment";

/**
 * Which segment a unit belongs to.
 *
 * Co-Living is decided by the normaliser the domain helpers already use;
 * everything else is a business apartment. There is no third bucket on purpose:
 * the unit form offers exactly two types ("Apartment" / "Co-Living"), and an
 * unrecognised type must still appear somewhere rather than vanish from the list.
 *
 * @param {object|null|undefined} unit
 * @returns {UnitSegment}
 */
export function getUnitSegment(unit) {
  return normalizeUnitTypeLabel(unit?.type) === "Co-Living"
    ? SEGMENT_CO_LIVING
    : SEGMENT_BUSINESS_APARTMENT;
}

/** First non-empty trimmed string, else "". */
function firstNonEmpty(...values) {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return "";
}

/**
 * The two address lines the later row renders.
 *
 * line1 is the street, line2 is "<PLZ> <Ort>". Both are "" when their parts are
 * missing — never the string "undefined", and never a stray separator: the
 * caller decides how to render an empty line, and a model that emits "undefined"
 * has already lost that choice. Field preference mirrors `normalizeUnit`
 * (`place` folds in `city`, `zip` folds in `postal_code`), with the raw API
 * spellings kept as fallbacks so an un-normalised row still reads correctly.
 *
 * @returns {{ addressLine1: string, addressLine2: string }}
 */
export function buildAddressLines(unit) {
  const street = formatDisplayAddress(
    firstNonEmpty(unit?.address, unit?.address_line1, unit?.street)
  );
  const zip = firstNonEmpty(unit?.zip, unit?.postal_code);
  const city = firstNonEmpty(unit?.place, unit?.city);

  return {
    addressLine1: String(street || "").trim(),
    addressLine2: [zip, city].filter(Boolean).join(" "),
  };
}

/** A usable numeric m² value, or null. Mirrors `roomSizeText`'s parse rules. */
function parseRoomSizeM2(room) {
  const raw = room?.size_m2;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return n;
}

/**
 * Total floor area, or null when it cannot be proven.
 *
 * FAIL-CLOSED BY DESIGN. A partial sum is worse than no number at all: rendered
 * as "186.3 m²" it looks exact, and nothing about it tells the reader that three
 * rooms were silently missing. So a figure is only returned when all three of
 * these hold:
 *
 *   1. the unit declares how many rooms it has (`unit.rooms` > 0),
 *   2. exactly that many room rows are loaded for it, and
 *   3. every one of those rows carries a usable positive `size_m2`.
 *
 * Anything else — rooms not loaded yet, a count mismatch, one missing size — is
 * null, which the UI shows as "—". Note what this means in practice: business
 * apartments have no room rows at all, so their area is null today. That is the
 * honest answer, not a gap to paper over.
 *
 * `unit.rooms` IS STILL THE ORACLE HERE, AND NO LONGER THE OCCUPANCY ONE. Until
 * OC2b this sentence said the two were the same number, which was true and was
 * the D5 defect: the occupancy badge judged itself against a declared field.
 * The badge now counts active rooms; this function deliberately keeps the
 * declared count, because the question it asks is a different one -- "do I have
 * a size for every room this unit claims to have" -- and answering it from the
 * active rows would call an area complete while a retired room's size is
 * missing. Area semantics are out of OC2b's scope; only this comment changed.
 *
 * @returns {number|null}
 */
export function computeTotalAreaM2(unit, rooms) {
  const expected = Math.floor(Number(unit?.rooms) || 0);
  if (expected <= 0) return null;

  const unitRooms = getRoomsForUnit(unit?.unitId ?? unit?.id, rooms);
  if (unitRooms.length !== expected) return null;

  let sum = 0;
  for (const room of unitRooms) {
    const size = parseRoomSizeM2(room);
    if (size == null) return null;
    sum += size;
  }

  // Guards against float drift turning 3 × 62.1 into 186.29999999999998.
  return Math.round(sum * 100) / 100;
}

/**
 * The CURRENT room count of a co-living unit, or 0 when it cannot be proven.
 *
 * Canonical counters first, the unit's active room rows otherwise -- the same
 * order of preference `getUnitOccupancyStatus` and `getCoLivingMetrics` apply,
 * so the three figures on one row cannot disagree. An explicit enrichment
 * failure returns 0, which `UnitListRow` renders as the dash it renders for a
 * unit with no rooms; inventing a number from `unit.rooms` instead is the one
 * thing OC1 ruled out.
 *
 * NOT `computeTotalAreaM2`'s input. That function keeps `unit.rooms` as its
 * completeness oracle on purpose (see its own docstring) and OC2b does not
 * touch area semantics.
 *
 * @returns {number}
 */
function coLivingRoomCount(unit, rooms) {
  if (hasOccupancySnapshotError(unit)) return 0;
  const canonical = canonicalUnitRoomCounts(unit);
  if (canonical) return canonical.total;
  return activeRoomsForUnit(unit, rooms).length;
}


/** A finite number, or null. Keeps "" and undefined out of the money fields. */
function numberOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Build the view model for one unit.
 *
 * `rooms` and `tenancies` are the FULL already-loaded collections, not
 * pre-filtered per unit — that matches how the page holds them and how the
 * helpers expect to receive them. `tenancies` of null means "not loaded yet",
 * which the occupancy helpers translate into a null status rather than a
 * confident "frei"; that distinction is preserved here.
 *
 * @param {{
 *   unit: object,
 *   rooms?: object[]|null,
 *   tenancies?: object[]|null,
 *   index?: number,
 *   resolveManagementLabel?: (unit: object) => string,
 * }} args
 */
export function buildUnitListItem({
  unit,
  rooms = [],
  tenancies = null,
  index,
  resolveManagementLabel,
} = {}) {
  if (!unit) return null;

  const segment = getUnitSegment(unit);
  const isCoLiving = segment === SEGMENT_CO_LIVING;

  const occupancyStatus = getUnitOccupancyStatus(unit, rooms, tenancies);
  const metrics = getCoLivingMetrics(unit, rooms, tenancies);
  const { addressLine1, addressLine2 } = buildAddressLines(unit);

  return {
    id: String(unit.unitId ?? unit.id ?? ""),
    displayId: getDisplayUnitId(unit, index),
    segment,

    addressLine1,
    addressLine2,

    // THE "Zimmer" COLUMN, which sits immediately beside the occupancy block
    // and is read as part of it.
    //
    // CO-LIVING: the canonical CURRENT room count (OC2b). It used to be the
    // declared `unit.rooms` field, and the comment here used to say that the
    // occupancy helper treated the same figure as its denominator -- which was
    // true, and was the D5 defect: a unit declaring nine rooms with four active
    // ones printed "Zimmer 9" next to "4 / 9 belegt". Both now come from the
    // same canonical population.
    //
    // BUSINESS APARTMENT: still the declared field, deliberately. The room axis
    // gives such a unit exactly ONE row -- the synthetic "Gesamte Wohnung" that
    // `create_initial_rooms_for_unit` writes -- so a canonical count would
    // print "Zimmer 1" for a three-room flat. `unit.rooms` is that flat's room
    // count, a property attribute, and it is not an occupancy denominator for
    // this segment: the occupancy block beside it is null here, and
    // `getUnitOccupancyStatus` answers a business apartment from its
    // unit-level tenancies without consulting rooms at all.
    roomCount: isCoLiving
      ? coLivingRoomCount(unit, rooms)
      : Math.floor(Number(unit.rooms) || 0),
    totalAreaM2: computeTotalAreaM2(unit, rooms),

    occupancyStatus,
    occupancyLabel: formatOccupancyStatusDe(occupancyStatus),

    // Room-level counts are only meaningful for co-living; a business apartment
    // has one contract, not a per-room split, so these stay null rather than
    // reporting a 0/0 that reads like an empty co-living unit.
    occupiedCount: isCoLiving ? metrics.occupiedCount : null,
    reservedCount: isCoLiving ? metrics.reservedCount : null,
    freeCount: isCoLiving ? metrics.freeCount : null,
    totalRooms: isCoLiving ? metrics.totalRooms : null,

    // See MONEY in the module header: the source differs per segment because it
    // differs per segment TODAY.
    currentRevenueChf: isCoLiving
      ? numberOrNull(metrics.currentRevenue)
      : numberOrNull(unit.current_revenue_chf),
    // WHAT THE UNIT COSTS US IN A NORMAL MONTH, read from the backend and never
    // recomputed here. This slot used to read `landlord_rent_monthly_chf`, a
    // column that lost its write path in Step 5.1 but kept `default=0` and a
    // read path — so the row printed a confident "CHF 0" for every FAHN unit
    // while the real figures sat in `unit_costs`. `running_costs_monthly_chf`
    // (F2a) is the recurring run-rate over those rows plus the landlord deposit
    // insurance premium; it is deliberately NOT prorated against a lease window,
    // because a reader comparing two rows is comparing units, not months.
    //
    // `null` is a real answer here and must survive as one: F2a returns it when
    // no classified recurring source exists, which is not the same statement as
    // "costs nothing". `numberOrNull` keeps an explicit 0 as 0 for exactly that
    // reason.
    runningCostsMonthlyChf: numberOrNull(unit.running_costs_monthly_chf),

    managementLabel:
      typeof resolveManagementLabel === "function"
        ? resolveManagementLabel(unit)
        : null,

    availableFrom: firstNonEmpty(unit.availableFrom, unit.available_from) || null,

    // The LANDLORD's lease start (Vertrag Vermieter), which is what the current
    // tables show under "Mietbeginn (Vertrag)". Named for what it is: calling it
    // a tenancy start would invite a later reader to pair it with tenant data it
    // has nothing to do with.
    landlordLeaseStartDate:
      firstNonEmpty(unit.leaseStartDate, unit.lease_start_date) || null,
  };
}

/**
 * Map a collection of units to view models.
 *
 * `index` is passed through so `getDisplayUnitId`'s legacy positional fallback
 * behaves exactly as it does when the tables map over their own arrays.
 */
export function buildUnitListItems({
  units,
  rooms = [],
  tenancies = null,
  resolveManagementLabel,
} = {}) {
  if (!Array.isArray(units)) return [];
  return units.map((unit, index) =>
    buildUnitListItem({ unit, rooms, tenancies, index, resolveManagementLabel })
  );
}
